/**
 * Cross-Border Enhancement
 * Enhances the existing currency selector to include additional countries
 * from the country-localization section (external stores)
 */

class CrossBorderEnhancement {
  constructor() {
    this.selectors = [];
    this.localizationData = null;
  }

  async init() {
    console.log('[Cross-Border] Initializing enhancement...');

    // Wait for localization data
    this.localizationData = await this.getLocalizationData();

    if (!this.localizationData) {
      console.log('[Cross-Border] No localization data available');
      return;
    }

    // Get all custom countries (isCustom = true)
    this.customCountries = this.localizationData.countries.filter(c => c.isCustom);

    if (this.customCountries.length === 0) {
      console.log('[Cross-Border] No additional countries configured');
      return;
    }

    console.log('[Cross-Border] Found custom countries:', this.customCountries);

    // Find all currency selectors on the page
    this.findSelectors();

    // Enhance each selector
    this.enhanceSelectors();

    // Handle clicks on custom countries
    this.attachEventListeners();
  }

  async getLocalizationData() {
    return new Promise((resolve) => {
      if (window.localizationData) {
        resolve(window.localizationData);
        return;
      }

      document.addEventListener('localization-data-ready', function handler() {
        document.removeEventListener('localization-data-ready', handler);
        resolve(window.localizationData);
      });

      // Timeout after 3 seconds
      setTimeout(() => resolve(null), 3000);
    });
  }

  findSelectors() {
    // Find all currency disclosure elements
    const disclosures = document.querySelectorAll('[data-disclosure-currency]');

    disclosures.forEach(disclosure => {
      const list = disclosure.querySelector('[data-disclosure-list]');
      const input = disclosure.querySelector('[data-disclosure-input]');

      if (list && input) {
        this.selectors.push({ disclosure, list, input });
      }
    });

    console.log('[Cross-Border] Found', this.selectors.length, 'currency selectors');
  }

  enhanceSelectors() {
    this.selectors.forEach(({ list }) => {
      // First, remove any Shopify market items that are duplicated in external stores
      // (Section blocks take priority over Shopify markets)
      const externalStoreCodes = this.customCountries.map(c => c.code.toUpperCase());

      const existingItems = list.querySelectorAll('.disclosure-list__item');
      existingItems.forEach(item => {
        const option = item.querySelector('[data-disclosure-option]');
        if (option) {
          const countryCode = option.getAttribute('data-value')?.toUpperCase();
          if (countryCode && externalStoreCodes.includes(countryCode)) {
            console.log(`[Cross-Border] Removing duplicate Shopify market: ${countryCode}`);
            item.remove();
          }
        }
      });

      // Now add external stores (which will be the only version of duplicated countries)
      this.customCountries.forEach(country => {
        // Create list item
        const li = document.createElement('li');
        li.className = 'disclosure-list__item';
        li.setAttribute('data-custom-country', country.code);

        // Create link
        const link = document.createElement('a');
        link.className = 'disclosure-list__option no-transition fs-body-75';
        link.href = '#';
        link.setAttribute('data-value', country.code);
        link.setAttribute('data-disclosure-option', '');
        link.setAttribute('data-custom-country-link', country.code);
        link.setAttribute('data-custom-url', country.url || '');

        // Add flag icon using same pattern as Shopify countries
        const flagSpan = document.createElement('span');
        flagSpan.className = `flag-icon flag-icon-${country.code.toLowerCase()}`;
        link.appendChild(flagSpan);

        // Add label
        const label = document.createElement('span');
        label.className = 'disclosure-list__option-label';
        label.textContent = `${country.name} (${country.currency} ${country.currencySymbol})`;
        link.appendChild(label);

        li.appendChild(link);
        list.appendChild(li);
      });

      console.log('[Cross-Border] Removed duplicates and added', this.customCountries.length, 'external stores to selector');
    });
  }

  attachEventListeners() {
    // Listen for clicks on custom country links
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-custom-country-link]');

      if (link) {
        event.preventDefault();
        event.stopPropagation();

        const countryCode = link.getAttribute('data-custom-country-link');
        const customUrl = link.getAttribute('data-custom-url');

        console.log('[Cross-Border] Custom country clicked:', countryCode, customUrl);

        if (customUrl) {
          // Set cookie
          const expirationDate = new Date();
          expirationDate.setTime(expirationDate.getTime() + (7 * 24 * 60 * 60 * 1000));
          document.cookie = `country_selected=true; expires=${expirationDate.toUTCString()}; path=/`;

          // Redirect to external store
          console.log('[Cross-Border] Redirecting to:', customUrl);
          window.location.href = customUrl;
        }
      }
    });

    console.log('[Cross-Border] Event listeners attached');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const enhancement = new CrossBorderEnhancement();
    enhancement.init();
  });
} else {
  const enhancement = new CrossBorderEnhancement();
  enhancement.init();
}
